import { Icon } from '@iconify/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TopicDto } from '@/Domain/api/Conversation/dto/Topic.dto';
import { TopicForm } from '@/Domain/components/Insights/Topics/TopicForm/TopicsForm';
import { TopicsGraphics } from '@/Domain/components/Insights/Topics/Topics.graphics';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useConversationTopicCreate } from '@/Domain/hooks/Conversation/useConversationTopicCreate.hook';

type Props = {
  topics: TopicDto[];
};

const MAX_TOPICS = 12;
export const MangeTopicsPage = ({ topics }: Props) => {
  const { t } = useTranslation('insights_page');

  const { handleCreateTopic, isPending } = useConversationTopicCreate();
  const hasEmptyTopic = topics.some(({ topic }) => !topic);

  useEffect(() => {
    if (!topics.length) {
      handleCreateTopic();
    }
  }, [topics.length]);

  return (
    <div className="py-6 space-y-6">
      <div className="rounded-xl bg-muted">
        <div className="grid grid-cols-[1fr_auto] gap-20">
          <div className="py-6 pl-6 pr-6 xl:pr-0 flex flex-col justify-between gap-1">
            <h3 className="font-semibold flex items-center gap-2">
              <Icon
                icon="mingcute:settings-3-line"
                className="bg-foreground-subtle rounded-full text-white p-1 text-sm box-content"
              />
              {t('manage_topics.title')}
            </h3>
            <p className="text-sm text-muted-foreground">{t('manage_topics.description')}</p>
            <a
              href="https://www.quickchat.ai/post/feature-announcement-topics"
              className="text-sm font-medium text-foreground flex items-center gap-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('manage_topics.learn_more')}
              <Icon icon="mingcute:arrow-right-up-line" />
            </a>
          </div>
          <section className="hidden xl:block pr-10 mt-auto">
            <TopicsGraphics />
          </section>
        </div>
      </div>

      <TooltipProvider>
        <div>
          <section className="grid grid-cols-[0.5fr_2fr_4fr_0.5fr] gap-x-2 py-3 px-4 text-foreground-subtle bg-muted border-b mb-4 text-sm font-medium">
            <span>{t('manage_topics.form.header.no')}</span>
            <span className="pl-1">{t('manage_topics.form.header.topic_name')}</span>
            <span className="pl-2.5">{t('manage_topics.form.header.description')}</span>
          </section>

          {topics.map((topic, index) => (
            <TopicForm key={topic.id} topic={topic} index={index} topicLength={topics.length} />
          ))}
          <Separator />
          <div className="flex justify-between items-center mt-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    size="sm"
                    variant="dark"
                    disabled={topics.length >= MAX_TOPICS || hasEmptyTopic || isPending}
                    onClick={handleCreateTopic}
                  >
                    <Icon icon="mingcute:add-line" />
                    {t('button.add_topic')}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {hasEmptyTopic
                  ? t('tooltip.no_more_empty_topic')
                  : topics.length >= MAX_TOPICS
                    ? t('tooltip.all_topic_used', { maxTopics: MAX_TOPICS })
                    : t('tooltip.add_topic')}
              </TooltipContent>
            </Tooltip>
            <p className="text-sm text-foreground-subtle flex items-center">
              {t('manage_topics.remaining_topics')}
              <span className="font-medium ml-1">{MAX_TOPICS - topics.length}</span>
            </p>
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
};
